import VendorDetails from '../components/Returns/Define/components/VendorDetails'
import VendorLdcDetails from '../components/Returns/Define/components/VendorLdcDetails'
import {
  COUNTRY_LIST,
  VENDOR_STATUS_OPTIONS,
  RESIDENTIAL_STATUS_OPTIONS,
  TAXATION_REGIME_OPTIONS,
  PAN_AADHAR_OPTIONS,
  formatIndianNumber,
} from './index.js'
import { z } from 'zod'

const addressSchema = z
  .string()
  .max(150, 'Address must be at most 150 characters')
  .regex(/^[^^]*$/, 'Caret character (^) is not allowed')
  .optional()
  .nullable()

const address50Schema = z
  .string()
  .max(50, 'Must be at most 50 characters')
  .regex(/^[^^]*$/, 'Caret character (^) is not allowed')
  .optional()
  .nullable()

const address60Schema = z
  .string()
  .max(60, 'Must be at most 60 characters')
  .regex(/^[^^]*$/, 'Caret character (^) is not allowed')
  .optional()
  .nullable()

const caretSchema = z
  .string()
  .max(26, 'Must be at most 26 characters')
  .regex(/^[^^]*$/, 'Caret character (^) is not allowed')
  .optional()
  .nullable()

const pincodeSchema = z
  .string()
  .max(15, 'PIN Code must be at most 15 characters')
  .regex(/^[^^]*$/, 'Caret character (^) is not allowed')
  .or(z.literal(''))
  .optional()
  .nullable()

const stateDeducteeSchema = z
  .string()
  .max(25, 'State of the Deductee must be at most 25 characters')
  .regex(/^[^^]*$/, 'Caret character (^) is not allowed')
  .optional()
  .nullable()

const misFuString200 = z
  .string()
  .max(200, 'must be at most 200 characters allowed')
  .optional()
  .nullable()

const misSchemaFields = Object.fromEntries(
  Array.from({ length: 20 }, (_, i) => [`mis${i + 1}`, misFuString200])
)

const fuSchemaFields = Object.fromEntries(
  Array.from({ length: 20 }, (_, i) => {
    const num = String(i + 1).padStart(2, '0')
    return [`fu_${num}`, misFuString200]
  })
)

export const vendorSchema = z.object({
  vendorName: z
    .string()
    .min(1, 'Vendor Name is required')
    .max(75, 'Vendor Name must be at most 75 characters')
    .regex(/^[^^]*$/, 'Caret character (^) is not allowed'),
  vendorStatus: z.enum(VENDOR_STATUS_OPTIONS.map(o => o.value)),
  residentialStatus: z.enum(RESIDENTIAL_STATUS_OPTIONS.map(o => o.value)),

  taxationRegime: z.enum(TAXATION_REGIME_OPTIONS.map(o => o.value)),
  panAadhar: z.enum(PAN_AADHAR_OPTIONS.map(o => o.value)),

  address: addressSchema,
  address1: address60Schema,
  address2: address50Schema,
  pincode: pincodeSchema,
  stateOfTheDeductee: stateDeducteeSchema,
  tinUin: caretSchema,

  country: z
    .enum(COUNTRY_LIST.map(o => o.value))
    .optional()
    .nullable(),
  emailId: z
    .string()
    .trim()
    .transform(val => (val === '' ? undefined : val))
    .optional()
    .nullable()
    .refine(val => !val || z.string().email().safeParse(val).success, {
      message: 'Invalid Email format',
    }),
  contactNumber: z
    .string()
    .trim()
    .transform(val => (val === '' ? undefined : val))
    .optional()
    .nullable()
    .refine(val => !val || /^[0-9]{10,15}$/.test(val), {
      message: 'Contact number must be 10–15 digits',
    }),

  ...misSchemaFields,
  ...fuSchemaFields,
})

const alphanumericRegex = /^[a-zA-Z0-9]*$/
const numericRegex = /^\d+(\.\d{1,2})?$/
const dateRegex = /^([0-2]\d|3[01])\/(0\d|1[0-2])\/\d{4}$/

export const vendorLdcDetailsSchema = z
  .object({
    entityTan: z
      .string()
      .nonempty('Entity TAN is required')
      .max(10, 'Max 10 characters allowed')
      .regex(alphanumericRegex, 'Only alphanumeric allowed'),

    vendorPan: z
      .string()
      .nonempty('Vendor PAN is required')
      .max(10, 'Max 10 characters allowed')
      .regex(alphanumericRegex, 'Only alphanumeric allowed'),

    section: z
      .string()
      .nonempty('Section is required')
      .max(55, 'Max 55 characters allowed')
      .regex(
        /^[A-Za-z0-9!@#$%&*()_+\-=\[\]{};':"\\|,.<>\/?~` ]{1,55}$/,
        'Section code must be up to 55 alphanumeric or special characters (except caret)'
      ),

  ldcNo: z
    .string()
    .nonempty('LDC No. is required')
    .max(15, 'Max 15 characters allowed')
    .regex(alphanumericRegex, 'Only alphanumeric allowed'),

    ldcLimit: z
      .union([z.string(), z.number()])
      .refine(val => !isNaN(Number(val)), 'Should be a numeric value')
      .refine(
        val => /^\d{1,12}(\.\d{1,2})?$/.test(String(val).trim()),
        'Max 12 integer digits and 2 decimal places allowed'
      ),

    manualAdjustment: z
      .union([z.string(), z.number()])
      .optional()
      .nullable()
      .default(0)
      .refine(val => {
        const num = Number(val)
        return !isNaN(num) && num >= 0
      }, 'Should be a positive numeric value')
      .refine(val => {
        const num = Number(val)
        return Number.isInteger(num * 100)
      }, 'Only up to 2 decimal places allowed'),

    amountUtilised: z
      .union([z.string(), z.number()])
      .optional()
      .nullable()
      .default(0)
      .refine(
        val => !isNaN(Number(val)),
        'Should be a numeric value with max 2 decimals'
      ),

    ldcStartDate: z
      .string()
      .nonempty('LDC Start Date is required')
      .regex(dateRegex, 'Invalid date format, use DD/MM/YYYY'),

    ldcEndDate: z
      .string()
      .nonempty('LDC End Date is required')
      .regex(dateRegex, 'Invalid date format, use DD/MM/YYYY'),

    prescribedRate: z
      .union([z.string(), z.number()])
      .refine(val => !isNaN(Number(val)), 'Should be a numeric value')
      .refine(
        val => /^\d{1,3}(\.\d{1,2})?$/.test(String(val).trim()),
        'Max 3 integer digits and 2 decimal places allowed'
      ),
    ...misSchemaFields,
    ...fuSchemaFields,
  })
  .superRefine((data, ctx) => {
    const ldcLimit = Number(data.ldcLimit || 0)
    const amountUtilised = Number(data.amountUtilised || 0)
    const manualAdjustment = Number(data.manualAdjustment || 0)

    /** Rule 1: INVALID when LDC Limit < Amount Utilised + Manual Adjustment */
    if (ldcLimit < amountUtilised + manualAdjustment) {
      ctx.addIssue({
        path: ['ldcLimit'],
        message:
          'LDC Limit must be greater than or equal to Amount Utilised + Manual Adjustment',
        code: z.ZodIssueCode.custom,
      })
    }

    /** Rule 2: INVALID when Manual Adjustment > (LDC Limit - Amount Utilised) */
    if (manualAdjustment > ldcLimit - amountUtilised) {
      ctx.addIssue({
        path: ['manualAdjustment'],
        message:
          'Manual Adjustment cannot exceed (LDC Limit − Amount Utilised)',
        code: z.ZodIssueCode.custom,
      })
    }
  })

export const VENDOR_DETAILS_COLUMNS = [
  { id: 'entityTan', label: 'Entity tan', width: 200 }, // Non-editable, unique ID
  { id: 'fu_01', label: 'Vendor code', width: 150 },
  { id: 'vendorPan', label: 'Vendor pan', width: 200 },
  { id: 'vendorName', label: 'Vendor name', width: 200, editable: true },
  {
    id: 'vendorStatus',
    label: 'Vendor status',
    width: 200,
    editable: true,
    type: 'dropdown',
    options: VENDOR_STATUS_OPTIONS,
  },
  {
    id: 'residentialStatus',
    label: 'Residential status',
    width: 200,
    editable: true,
    type: 'dropdown',
    options: RESIDENTIAL_STATUS_OPTIONS,
  },
  {
    id: 'vendorType',
    label: 'Vendor type',
    width: 200,
  },
  { id: 'tinUin', label: 'TIN/UIN', width: 180, editable: true },
  {
    id: 'taxationRegime',
    label: 'Opting 115BAC(1A) taxation regime',
    width: 200,
    editable: true,
    type: 'dropdown',
    align: 'left',
    options: TAXATION_REGIME_OPTIONS,
  },
  {
    id: 'panAadhar',
    label: 'PAN linked with aadhar',
    width: 200,
    editable: true,
    type: 'dropdown',
    options: PAN_AADHAR_OPTIONS,
  },
  {
    id: 'address',
    label: 'Address',
    width: 250,
    editable: row => ['NR', 'R'].includes(row.residentialStatus),
  },
  {
    id: 'address1',
    label: 'Address1',
    width: 250,
    editable: row => ['NR', 'R'].includes(row.residentialStatus),
  },
  {
    id: 'address2',
    label: 'Address2',
    width: 250,
    editable: row => ['NR', 'R'].includes(row.residentialStatus),
  },
  {
    id: 'stateOfTheDeductee',
    label: 'State of the deductee',
    width: 250,
    editable: true,
  },
  {
    id: 'pincode',
    label: 'PIN code',
    width: 120,
    editable: true,
    isFormatted: false,
  },
  {
    id: 'country',
    label: 'Country',
    width: 200,
    editable: true,
    maxWidth:200,
    type: 'dropdown',
    options: COUNTRY_LIST,
  },
  { id: 'emailId', label: 'Email id', width: 200, editable: true },
  {
    id: 'contactNumber',
    label: 'Contact number',
    width: 200,
    editable: true,
    isNumeric: true,
    isFormatted: false,
  },
  ...Array.from({ length: 20 }, (_, i) => ({
    id: `mis${i + 1}`,
    label: `MIS ${i + 1}`,
    minWidth: 100,
    editable: true,
  })),
  ...Array.from({ length: 19 }, (_, i) => {
    const num = String(i + 2).padStart(2, '0')
    return {
      id: `fu_${num}`,
      label: `FU_${num}`,
      minWidth: 100,
      editable: true,
    }
  }),
]

export const VENDOR_LDC_DETAILS_COLUMNS = [
  { id: 'entityTan', label: 'Entity tan', width: 150 },
  { id: 'fu_01', label: 'Vendor code', width: 150 },
  { id: 'vendorPan', label: 'Vendor pan', width: 150 },
  { id: 'section', label: 'Section', width: 150, editable: true },
  {
    id: 'ldcNo',
    label: 'Ldc no.',
    width: 200,
    editable: true,
    isFormatted: false,
  },
  { id: 'ldcStartDate', label: 'Ldc start date', width: 200, editable: true },
  { id: 'ldcEndDate', label: 'Ldc end date', width: 250, editable: true },
  {
    id: 'prescribedRate',
    label: 'Prescribed rate',
    width: 150,
    editable: true,
  },
  { id: 'ldcLimit', label: 'Ldc limit', width: 150, editable: true },
  {
    id: 'amountUtilised',
    label: 'Amount utilised',
    width: 150,
  },
  {
    id: 'manualAdjustment',
    label: 'Manual adjustment',
    width: 200,
    editable: true,
  },
  {
    id: 'balanceAvailable',
    label: 'Balance available',
    width: 200,
  },
  ...Array.from({ length: 20 }, (_, i) => ({
    id: `mis${i + 1}`,
    label: `MIS ${i + 1}`,
    minWidth: 100,
    editable: true,
  })),
  ...Array.from({ length: 19 }, (_, i) => {
    const num = String(i + 2).padStart(2, '0')
    return {
      id: `fu_${num}`,
      label: `FU_${num}`,
      minWidth: 100,
      editable: true,
    }
  }),
]

const MONTH_NAMES = [
  '',
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

/**
 * Builds a grouped "Amount Utilised" column with monthly sub-columns
 * based on the monthlyAmountUtilised data present across all table rows.
 *
 * @param {Array} tableData - Array of LDC row objects from the API
 * @returns {Object} A grouped column definition with monthly sub-columns
 */
export function buildMonthlyColumns(tableData) {
  const monthSet = new Set()

  // Scan all rows to collect unique year-month pairs
  if (Array.isArray(tableData)) {
    tableData.forEach(row => {
      const monthly = row?.monthlyAmountUtilised
      if (monthly && typeof monthly === 'object') {
        Object.keys(monthly).forEach(year => {
          const yearNum = Number(year)
          if (!Number.isFinite(yearNum) || !Number.isInteger(yearNum)) return

          const months = monthly[year]
          if (months && typeof months === 'object') {
            Object.keys(months).forEach(month => {
              const monthNum = Number(month)
              if (
                !Number.isFinite(monthNum) ||
                !Number.isInteger(monthNum) ||
                monthNum < 1 ||
                monthNum > 12
              )
                return
              monthSet.add(`${yearNum}_${monthNum}`)
            })
          }
        })
      }
    })
  }

  // Sort chronologically
  const sortedPairs = Array.from(monthSet)
    .map(key => {
      const [year, month] = key.split('_').map(Number)
      return { year, month }
    })
    .sort((a, b) => a.year - b.year || a.month - b.month)

  // Build sub-columns
  const MONTH_COL_WIDTH = 150
  const columns = sortedPairs.map(({ year, month }) => ({
    id: `month_${year}_${month}`,
    label: `${MONTH_NAMES[month]} ${year}`,
    width: MONTH_COL_WIDTH,
    minWidth: MONTH_COL_WIDTH,
    align: 'right',
    render: (_value, row) => {
      const monthly = row?.monthlyAmountUtilised
      if (!monthly || typeof monthly !== 'object') return ''
      const yearData = monthly[String(year)]
      if (!yearData || typeof yearData !== 'object') return ''
      const val = yearData[String(month)]
      if (val === undefined || val === null) return ''
      return formatIndianNumber(val)
    },
  }))

  return {
    id: 'amountUtilised',
    label: 'Amount utilised',
    type: 'group',
    // With tableLayout: 'fixed', the group header spans all sub-columns, so its
    // width must equal the sum of the sub-column widths — otherwise the default
    // width gets divided across months and squishes the headers.
    width: columns.length * MONTH_COL_WIDTH,
    columns,
  }
}

export const VENDOR_MASTER_TABS = [
  {
    label: 'Vendor Details',
    content: VendorDetails,
    columns: VENDOR_DETAILS_COLUMNS,
  },
  {
    label: 'Vendor LDC Details',
    content: VendorLdcDetails,
    columns: VENDOR_LDC_DETAILS_COLUMNS,
  },
]

const ddMMYYYYWithSlash = z
  .string()
  .regex(
    /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/,
    'Invalid format. Example: 21/10/2024 (dd/mm/yyyy)'
  )

export const VENDOR_DETAILS_SEARCH_FIELDS = VENDOR_DETAILS_COLUMNS.map(col => {
  // If it's a dropdown in columns, also keep it dropdown in search
  if (col.type === 'dropdown') {
    return {
      ...col,
      type: 'dropdown',
      options: col.options || [],
      placeholder: `Select ${col.label}`,
    }
  }

  // Default text search
  return {
    ...col,
    type: 'text',
    placeholder: `Enter ${col.label}`,
  }
})

export const VENDOR_LDC_SEARCH_FIELDS = [
  ...VENDOR_LDC_DETAILS_COLUMNS.map(col => {
    // Special handling for dates
    if (col.id === 'ldcStartDate' || col.id === 'ldcEndDate') {
      return {
        ...col,
        type: 'text',
        placeholder: `Example: 24/11/2019 (dd/mm/yyyy)`,
        zod: ddMMYYYYWithSlash,
      }
    }
    return {
      id: col.id,
      label: col.label,
      type: col.type || 'text',
      placeholder: `Enter ${col.label}`,
    }
  }),
]
